'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Heading } from './useHeadings';

const SCROLL_BUFFER = 20; // extra pixels below sticky header to trigger "active" state earlier
const SCROLL_TO_BUFFER = 10; // gap between heading and viewport top when scrolling to a section

interface UseScrollBehaviorOptions {
  headings: Heading[];
  stickyTop: number; // height of sticky header (used to offset scroll calculations)
  isFloating: boolean; // true when sidebar is in desktop/floating mode vs mobile dropdown
}

/**
 * Manages scroll-related behavior for the jump links component:
 * - Tracks which heading is currently "active" based on scroll position
 * - Provides smooth scrolling to headings when clicked
 * - Auto-scrolls the jump links list to keep the active item visible (desktop only)
 */
export const useScrollBehavior = ({ headings, stickyTop, isFloating }: UseScrollBehaviorOptions) => {
  const [activeId, setActiveId] = useState<string>('');
  const listRef = useRef<HTMLDivElement>(null);
  const headingsRef = useRef<Heading[]>([]);

  // keep headingsRef in sync for scroll handler
  useEffect(() => {
    headingsRef.current = headings;
  }, [headings]);

  // track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const currentHeadings = headingsRef.current;
      if (currentHeadings.length === 0) return;

      // calculate the "trigger line" - the point where we consider a heading to be active
      // (just below the sticky header)
      const triggerLine = window.scrollY + stickyTop + SCROLL_BUFFER;

      // find the last heading that's above the trigger line
      // (headings are in DOM order, so we iterate and keep updating until we pass the trigger)
      let activeHeading = currentHeadings[0]?.id || '';

      for (const { id, element } of currentHeadings) {
        const elementTop = element.getBoundingClientRect().top + window.scrollY;

        if (elementTop <= triggerLine) {
          activeHeading = id;
        } else {
          break; // headings are ordered, so no need to check further
        }
      }

      setActiveId(activeHeading);
    };

    handleScroll(); // initial check on mount
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [stickyTop]);

  // auto-scroll the jump links list to keep active item visible (desktop sidebar only)
  const scrollActiveIntoView = useCallback(
    (isOpen: boolean) => {
      // skip if: mobile mode, no active heading, container not mounted, or list is collapsed
      if (!isFloating || !activeId || !listRef.current || !isOpen) return;

      const container = listRef.current;
      const activeItem = container.querySelector(`[data-jump-link-id="${activeId}"]`) as HTMLElement | null;
      if (!activeItem) return;

      // use container-relative scrolling instead of scrollIntoView()
      // to avoid accidentally scrolling the entire page
      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();

      const isAboveVisibleArea = itemRect.top < containerRect.top;
      const isBelowVisibleArea = itemRect.bottom > containerRect.bottom;

      // scroll just enough to bring the item into view
      if (isAboveVisibleArea) {
        container.scrollTop += itemRect.top - containerRect.top;
      } else if (isBelowVisibleArea) {
        container.scrollTop += itemRect.bottom - containerRect.bottom;
      }
    },
    [activeId, isFloating]
  );

  // scroll to a specific heading when user clicks a jump link
  const scrollToHeading = useCallback(
    (id: string) => {
      // use getElementById for fresh DOM lookup, stored element refs can become stale after re-renders
      const element = document.getElementById(id);
      if (!element) return;

      // calculate scroll position: element's position minus sticky header height and buffer
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const targetScrollPosition = Math.max(0, elementTop - stickyTop - SCROLL_TO_BUFFER);

      window.scrollTo({ top: targetScrollPosition, behavior: 'smooth' });
      setActiveId(id); // immediately mark as active (don't wait for scroll handler)
    },
    [stickyTop]
  );

  return {
    activeId,
    listRef,
    scrollToHeading,
    scrollActiveIntoView,
  };
};
